import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapPin, Search, Eye, RefreshCw, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchSuburbResearchAnalytics } from '../api/analyticsApi';

// Headline number card
const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
            <Icon className="w-4 h-4" style={{ color }} /> {label}
        </div>
        <div className="text-2xl font-bold text-gray-900">{(value ?? 0).toLocaleString()}</div>
    </div>
);

// Horizontal bar ranking list
const BarList = ({ items, color, labelFor, valueFor, subFor, emptyText }) => {
    const max = Math.max(1, ...items.map(valueFor));
    if (!items.length) {
        return <p className="text-xs text-gray-400 py-6 text-center">{emptyText}</p>;
    }
    return (
        <div className="space-y-2.5">
            {items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-6 flex-shrink-0 text-right">{idx + 1}</span>
                    <span className="text-xs text-gray-700 w-40 flex-shrink-0 truncate" title={labelFor(item)}>
                        {labelFor(item)}
                        {subFor && subFor(item) && (
                            <span className="text-gray-400"> · {subFor(item)}</span>
                        )}
                    </span>
                    <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                        <div className="h-4 rounded-full"
                             style={{ width: `${Math.max(6, (valueFor(item) / max) * 100)}%`, background: color }} />
                    </div>
                    <span className="text-xs font-semibold text-gray-900 w-12 text-right">
                        {valueFor(item).toLocaleString()}
                    </span>
                </div>
            ))}
        </div>
    );
};

// Simple daily activity mini bar chart (views + searches stacked side by side)
const DailyChart = ({ daily }) => {
    const rows = daily || [];
    const max = Math.max(1, ...rows.map((d) => (d.views || 0) + (d.searches || 0)));
    if (!rows.length) {
        return <p className="text-xs text-gray-400 py-6 text-center">No activity recorded in this range yet.</p>;
    }
    return (
        <div>
            <div className="flex items-end gap-0.5 h-32">
                {rows.map((d, i) => {
                    const total = (d.views || 0) + (d.searches || 0);
                    const h = (total / max) * 100;
                    const viewsH = total ? ((d.views || 0) / total) * h : 0;
                    const searchH = h - viewsH;
                    return (
                        <div key={i} className="flex-1 flex flex-col justify-end min-w-0" title={`${d.date}: ${d.views} views · ${d.searches} searches`}>
                            <div style={{ height: `${searchH}%`, background: '#f59e0b' }} className="rounded-t-sm" />
                            <div style={{ height: `${viewsH}%`, background: '#6366f1' }} />
                        </div>
                    );
                })}
            </div>
            <div className="flex items-center gap-4 mt-3 text-[11px] text-gray-500">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#6366f1] inline-block" /> Suburb opened</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#f59e0b] inline-block" /> Search typed</span>
            </div>
        </div>
    );
};

/**
 * Suburb Research analytics — what suburbs buyers explore on the portal's
 * CoinMarketCap-style Research page. Most-opened suburbs, raw search queries and
 * daily activity, from the tracking events the Research page fires.
 */
const SuburbResearchPanel = ({ startDate, endDate }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const loadTokenRef = useRef(0);

    useEffect(() => {
        const myToken = ++loadTokenRef.current;
        setLoading(true);
        fetchSuburbResearchAnalytics(startDate, endDate)
            .then((res) => { if (myToken === loadTokenRef.current) setData(res); })
            .catch((err) => {
                if (myToken !== loadTokenRef.current) return;
                console.error('Suburb research analytics load failed:', err);
                toast.error('Failed to load suburb research analytics');
            })
            .finally(() => { if (myToken === loadTokenRef.current) setLoading(false); });
    }, [startDate, endDate]);

    const totals = data?.totals || {};
    const topViewed = useMemo(() => data?.top_viewed || [], [data]);
    const topSearched = useMemo(() => data?.top_searched || [], [data]);

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center py-12 text-gray-500">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading suburb research activity…
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Headline numbers */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Eye} label="Suburbs opened" value={totals.total_views} color="#6366f1" />
                <StatCard icon={Search} label="Searches typed" value={totals.total_searches} color="#f59e0b" />
                <StatCard icon={MapPin} label="Distinct suburbs" value={totals.unique_suburbs} color="#10b981" />
                <StatCard icon={TrendingUp} label="Unique sessions" value={totals.unique_sessions} color="#0891b2" />
            </div>

            {/* Daily activity */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-indigo-600" /> Daily research activity
                </h3>
                <p className="text-xs text-gray-400 mb-4">Suburbs opened vs searches typed, per day</p>
                <DailyChart daily={data?.daily} />
            </div>

            {/* Rankings */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-indigo-600" /> Most-researched suburbs
                    </h3>
                    <p className="text-xs text-gray-400 mb-4">Which suburb terminals buyers open most</p>
                    <BarList
                        items={topViewed}
                        color="#6366f1"
                        labelFor={(i) => `${i.suburb}${i.state ? `, ${i.state}` : ''}`}
                        valueFor={(i) => i.views || 0}
                        subFor={(i) => (i.unique_sessions ? `${i.unique_sessions} sessions` : '')}
                        emptyText="No suburbs opened in this range yet."
                    />
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                        <Search className="w-4 h-4 text-amber-500" /> Top search queries
                    </h3>
                    <p className="text-xs text-gray-400 mb-4">Exactly what buyers type into the search box</p>
                    <BarList
                        items={topSearched}
                        color="#f59e0b"
                        labelFor={(i) => i.query}
                        valueFor={(i) => i.count || 0}
                        subFor={(i) => (i.unique_sessions ? `${i.unique_sessions} sessions` : '')}
                        emptyText="No searches typed in this range yet."
                    />
                </div>
            </div>

            <p className="text-[11px] text-gray-400">
                Sourced from the portal Research page. While the page is admin-gated, this reflects staff exploration;
                once it launches to buyers it will show real buyer demand for suburb intelligence.
            </p>
        </div>
    );
};

export default SuburbResearchPanel;
